// components/portal/InventoryListToolbar.tsx
'use client';

import React from 'react';
import { Plus } from '@/components/ui/lucide-memo';
import { InventorySearch, InventorySortDropdown } from './InventoryUI';
import {
  InventoryFilterControls,
  type InventoryFilterOption,
} from './InventoryFilterControls';

const EMPTY_FILTER_OPTIONS: InventoryFilterOption[] = [];

export interface InventoryListToolbarProps {
  beforeCreateContent?: React.ReactNode;
  onCreateClick?: () => void;
  createButtonLabel?: string;
  searchValue: string;
  /** The caller renders its own search (the Database folder browsers do), hide this one. */
  hideSearch?: boolean;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  // ponytail: ignored since the bar lost its refresh button (every list is live); drop with the
  // callers' refresh plumbing.
  disableManualRefresh?: boolean;
  refreshIsLoading?: boolean;
  onManualRefresh?: () => void;
  beforeFilterContent?: React.ReactNode;
  filterOptions?: InventoryFilterOption[];
  viewFilters: Record<string, unknown>;
  isFilterKeyLocked: (key: string) => boolean;
  mergeWithLockedFilters: (candidate: Record<string, unknown>) => Record<string, unknown>;
  setFilters: (next: Record<string, unknown>) => void;
  sortOptions: { label: string; field: string }[];
  activeSortField: string;
  activeSortDir: string;
  onSortChange: (field: string) => void;
  currentSortLabel: string;
  renderSortIcon?: (label: string) => React.ReactNode;
  afterSortContent?: React.ReactNode;
  rightToolbarContent?: React.ReactNode;
  /** A page-level view switch (SegmentSwitch: Slides Dashboard / Training, List / Factory, Source
   *  Shortlist / Assets). It sits in the title row just before the create pill, in every module; the
   *  row below holds the list's own chips. */
  viewSwitch?: React.ReactNode;
  /** The page's own heading. With it the bar is ChatGPT's page header: the title on the left, the
   *  pickers and the create pill on the right, the icon buttons on a row below. */
  title?: string;
}

export const InventoryListToolbar = ({
  beforeCreateContent,
  onCreateClick,
  createButtonLabel,
  searchValue,
  hideSearch = false,
  onSearchChange,
  searchPlaceholder = 'Search...',
  beforeFilterContent,
  filterOptions,
  viewFilters,
  isFilterKeyLocked,
  mergeWithLockedFilters,
  setFilters,
  sortOptions,
  activeSortField,
  activeSortDir,
  onSortChange,
  currentSortLabel,
  renderSortIcon,
  afterSortContent,
  rightToolbarContent,
  viewSwitch,
  title,
}: InventoryListToolbarProps) => {
  const createButton = onCreateClick ? (
    <button
      type="button"
      onClick={onCreateClick}
      className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#F0F0F0] px-3.5 font-medium text-black transition-colors hover:bg-white"
      style={{ fontSize: 14 }}
    >
      <Plus size={16} strokeWidth={2.2} />
      {createButtonLabel && <span style={{ fontSize: 'inherit' }}>{createButtonLabel}</span>}
    </button>
  ) : null;
  const trailing = rightToolbarContent || viewSwitch || createButton ? (
    <div className="ml-auto flex items-center gap-1">
      {rightToolbarContent}
      {viewSwitch ? <span className="ml-1.5">{viewSwitch}</span> : null}
      {createButton ? <span className="ml-1.5">{createButton}</span> : null}
    </div>
  ) : null;
  // No border-b on this container: the rule under the toolbar read as a seam
  // across EVERY dashboard that uses this bar (Slides, Analyst Tables, and the
  // shared Grid/Table), and the padding already separates it from the content.
  // Removed deliberately for all of them, not just the one it was reported on.
  // ChatGPT's Library row, the Source dashboard's own: the view's chips on the left; on the right a
  // labelled sort and a search field that is simply there. No refresh: the list is live. Untitled
  // tables get the same row (owner, 2026-09-30: the bare icon strip is gone), with the create
  // pill after the search.
  const controls = (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div className="-ml-1 flex min-w-0 flex-wrap items-center gap-1">
        {beforeCreateContent}
        {beforeFilterContent}
        <InventoryFilterControls
          filterOptions={filterOptions ?? EMPTY_FILTER_OPTIONS}
          viewFilters={viewFilters}
          isFilterKeyLocked={isFilterKeyLocked}
          mergeWithLockedFilters={mergeWithLockedFilters}
          setFilters={setFilters}
        />
        {afterSortContent}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <InventorySortDropdown
          options={sortOptions}
          activeField={activeSortField}
          activeDir={activeSortDir}
          onChange={onSortChange}
          currentLabel={currentSortLabel}
          renderIcon={renderSortIcon}
          pill
        />
        {hideSearch ? null : (
          <InventorySearch value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} alwaysOpen />
        )}
        {title ? null : trailing}
      </div>
    </div>
  );

  return (
    // One rhythm for every module home (owner, 2026-09-30: 'create coherence between all the
    // modules'): 24px sides, 24px above the title, 16px to the chips, 32px to the content, which
    // therefore starts without top padding of its own.
    <div className={title ? 'relative z-20 px-6 pb-8 pt-6' : 'relative z-20 mt-0 px-4 py-3'}>
      {title ? (
        // 34px, the view switch's height, so the row below starts at the same line whatever the
        // title row holds.
        <div className="mb-4 flex h-[34px] w-full items-center gap-3">
          <h1 className="min-w-0 truncate font-medium text-white" style={{ fontSize: 26, lineHeight: '32px' }}>{title}</h1>
          {trailing}
        </div>
      ) : null}
      {controls}
    </div>
  );
};
